import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as yup from 'yup';

import Modal from '../../../common2/Modal';
import container from '../../../../container';
import { useYupValidation } from '../../../../hooks/useYupValidation';
import { useSelectedShopId } from '../../../../hooks/useSelectedShop';
import DORSigningLinkContent from './DORSigningLinkContent';
import DORSigningLinkEmailConfirmationContent from './DORSigningLinkEmailConfirmationContent';
import { CreateDorSigningLinkResponse } from '../../../../api/types';
import { useFlag } from '@unleash/proxy-client-react';

const schema = yup.object().shape({
  recipientEmail: yup.string().email().required().label('Consumer email'),
});

type DORSigningLinkModalProps = {
  show: boolean;
  onClose: () => void;
  selectedLanguage: string | null;
  shipmentId: number;
  defaultRecipientEmail: string;
};

function DORSigningLinkModal(props: DORSigningLinkModalProps) {
  const { show, onClose, selectedLanguage, shipmentId, defaultRecipientEmail } = props;

  const isEmailStepEnabled = useFlag('dor-signing-link-email-confirm-enabled');

  const shopId = useSelectedShopId();
  const [recipientEmail, setRecipientEmail] = useState(defaultRecipientEmail);
  const [isEmailConfirmed, setIsEmailConfirmed] = useState(!isEmailStepEnabled);

  const { errors, validate } = useYupValidation(schema);

  const mutation = useMutation<CreateDorSigningLinkResponse, Error, string>({
    mutationKey: ['createDorSigningLink', shopId, shipmentId, selectedLanguage],
    mutationFn: (language) =>
      container.touchpointsApi.createDorSigningLink(shopId, shipmentId, language, recipientEmail),
  });

  const onConfirmEmail = () => {
    if (validate({ recipientEmail }) && selectedLanguage) {
      setIsEmailConfirmed(true);
    }
  };

  useEffect(() => {
    if (isEmailConfirmed && selectedLanguage) {
      mutation.mutate(selectedLanguage);
    }
  }, [isEmailConfirmed]);

  const onRecipientEmailChange = (email: string) => {
    setRecipientEmail(email);
  };

  return (
    <Modal show={show} onClose={onClose}>
      {isEmailConfirmed ? (
        <DORSigningLinkContent mutation={mutation} onClose={onClose} />
      ) : (
        <DORSigningLinkEmailConfirmationContent
          onConfirmEmail={onConfirmEmail}
          errors={errors}
          onRecipientEmailChange={onRecipientEmailChange}
          recipientEmail={recipientEmail}
        />
      )}
    </Modal>
  );
}

export default React.memo(DORSigningLinkModal);
